/* jshint esversion: 9 */
/* global ZIEGE_VERSION */

import * as sdk from "matrix-js-sdk";
import {ClientEvent, EventStatus, HttpApiEvent, KnownMembership, MatrixEventEvent, RoomEvent, RoomMemberEvent, Visibility} from "matrix-js-sdk";
import {CryptoEvent} from "matrix-js-sdk/lib/crypto-api";
import {DecryptionFailureCode} from "matrix-js-sdk/lib/crypto-api";
import {DateProgressElement, ZiegeProgressElement} from "./progress.js";
import {CryptoSetup} from "./crypto-setup.js";
import {
    inviterOf, isAcceptableInvite, isAcceptableStatusUpdate, isZiegeRoom,
    MEGOLM_ENCRYPTION_ALGORITHM, ZIEGE_MARKER_STATE_EVENT_TYPE, ZIEGE_STATUS_UPDATE_MESSAGE_TYPE,
} from "./ziege-rooms.js";
import {
    authorizationResponse, clientOptsForSession, completeLogin, cryptoStorageKey, cryptoStorePrefix, DEFAULT_CRYPTO_STORE_PREFIX, discardLegacySession,
    discardUnusableSession, forgetSession, hasStoredSession, loadSession, returnToLoginStart, revokeSession, startLogin,
} from "./session.js";

import  {Elm} from "./ziegeelm"
import {deleteDB, saveData} from "./ziegedb";
import {connectHistory} from "./navigation.js";
import {showStartupAccount, showStartupReload, showStartupStatus, showStartupWarning} from "./startup-screen.js";
import {holdAppLock, releaseAppLock} from "./app-lock.js";

const CRYPTO_STORE_WARNING_MS = 8000;

// Unter diesem Schlüssel lag der Stand der App, bevor er je Konto getrennt wurde.
const LEGACY_STATE_KEY = "ziege";

const CRYPTO_STORE_SUFFIXES = ["::matrix-sdk-crypto", "::matrix-sdk-crypto-meta"];

// Krypto-Stores anderer Geräte: Sie stammen aus früheren Anmeldungen in diesem
// Browser. Läuft nur unter der Instanzsperre, es nutzt sie also niemand mehr.
async function deleteForeignCryptoStores(currentPrefix) {
    if (!indexedDB.databases) {
        return;
    }
    const names = (await indexedDB.databases()).map((db) => db.name);
    for (const name of names) {
        const suffix = CRYPTO_STORE_SUFFIXES.find((s) => name.endsWith(s));
        if (!suffix || name === currentPrefix + suffix) {
            continue;
        }
        console.info("Krypto-Store eines anderen Geräts gelöscht: ", name);
        await new Promise((resolve) => {
            const request = indexedDB.deleteDatabase(name);
            request.onsuccess = request.onerror = request.onblocked = () => resolve();
        });
    }
}


function b64ToUint8(b64) {
    const pad = "=".repeat((4 - b64.length % 4) % 4);
    const base64 = (b64 + pad).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(base64);
    return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}


class PushMaster {

    static permissions_needed = [
        {name: "notifications"},
        {name: "push", userVisibleOnly: true},
    ]

    constructor() {
        this.permission = "uknown";
        this.statusChangedCallback = undefined;

    }

    setCallback(callback) {
        this.statusChangedCallback = callback;
    }


    async recheckPermissions() {
        // safari does not call the update event on permissionstatus objects and also does not update permissionstatus objects.
        // so we have to requery every time.
        let newPushState = "granted"

        // Ohne Notification- oder Push-API (etwa Safari auf dem iPhone im Tab) gibt es
        // nichts zu erlauben; die App läuft trotzdem.
        if (!("Notification" in window) || !("PushManager" in window) || !navigator.permissions) {
            newPushState = "unsupported";
        } else {
            for (const permission of PushMaster.permissions_needed) {
                try {
                    const result = await navigator.permissions.query(permission);
                    newPushState = result.state;
                } catch (e) {
                    console.warn("Berechtigung nicht abfragbar: ", permission.name, e);
                    newPushState = "unsupported";
                }
                if (newPushState !== "granted") {
                    break;
                }
            }
        }

        if (this.permission !== newPushState) {

            this.permission = newPushState;
            if (this.statusChangedCallback) {
                this.statusChangedCallback(this.permission);
            }
        }

        return this.permission;
    }

    // Beim Abmelden: Das Abo endet beim Push-Dienst; der Push-Backend-Server bekommt
    // für diesen pushkey danach keine Zustellung mehr durch.
    static async unsubscribe() {
        try {
            const reg = await navigator.serviceWorker?.getRegistration();
            const subscription = await reg?.pushManager.getSubscription();
            await subscription?.unsubscribe();
        } catch (e) {
            console.warn("Push-Abo nicht beendet: ", e);
        }
    }

    async updatePush(interactive = false) {
        if (this.permission === "unsupported") {
            return;
        }

        if (this.permission === "prompt" && interactive) {
            await Notification.requestPermission();
        }


        const VAPID_PUBLIC_KEY = "BC4px33EfL2Gne1tP789xveawX59ZqiryWVnVJhzuArq4mIOnDt8mxV74Zl3-jkONQIRKwADn34V_2k1HMsZg_c"

        const reg = await navigator.serviceWorker.ready;

        let subscription = await reg.pushManager.getSubscription()

        if (!subscription && ((this.permission == "granted") || interactive)) {
            subscription = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: b64ToUint8(VAPID_PUBLIC_KEY),
            });
        }

        if (!subscription) {
            console.warn("No subscription... no push..")
            return;
        }

        await this.recheckPermissions() // fire status change for Safari, because Safari does not fire the event (Bug 259432)


        const PUSH_BACKEND_URL = "https://admin.ziege.social"

        const APP_ID = "social.ziege.texelclient";


        let pushkey = localStorage.getItem("ziege_pushkey");
        if (!pushkey) {

            const res = await fetch(PUSH_BACKEND_URL + "/v1/webpush/register", {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({
                    app_id: APP_ID,
                    subscription: subscription.toJSON(),
                }),
            });
            const data = await res.json();
            pushkey = data.pushkey;
            localStorage.setItem("ziege_pushkey", pushkey);
        }


        // Format a Date as UTC without timezone suffix, e.g. "2025-12-27T12:55:13"
        function toUtcNoZ(d) {
            const pad = (n) => String(n).padStart(2, "0");
            return (
                d.getUTCFullYear() +
                "-" + pad(d.getUTCMonth() + 1) +
                "-" + pad(d.getUTCDate()) +
                "T" + pad(d.getUTCHours()) +
                ":" + pad(d.getUTCMinutes()) +
                ":" + pad(d.getUTCSeconds())
            );
        }

        let requestPush = async (time, title, repeat, interval, body) => {
            const payload = {
                title,
                body,
                first_fire_at: toUtcNoZ(time),
                repeat_count: repeat, // nur ein Repeat
                interval_hours: interval,
            };

            const url = `${PUSH_BACKEND_URL}/v1/push/${pushkey}/timers/0`;
            const res = await fetch(url, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });


        }


        // activate/update daily push


        let tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000)
        const min_hour = 9;
        const max_hour = 19;

        if (tomorrow.getHours() < min_hour) {
            tomorrow.setHours(min_hour);
        }

        if (tomorrow.getHours() > max_hour) {
            tomorrow.setHours(max_hour);
        }

        await requestPush(tomorrow, "Drück die Ziege!", 10, 24, "Die Ziege braucht Aufmerksamkeit! Schau mal!")
    }
}


async function createRoom(client, otherUserId){
    const room = await client.createRoom({
        visibility: Visibility.Private,
        initial_state: [{
            type: ZIEGE_MARKER_STATE_EVENT_TYPE,
            state_key: "",
            content: {
                is_ziege: true,
            }
        },
            {
                type: "m.room.encryption",
                state_key: "",
                content: {
                    algorithm: MEGOLM_ENCRYPTION_ALGORITHM
                }
            }
        ]
    })

    await client.invite(room.room_id, otherUserId, "Ziege benutzen")
    await waitForMembership(client, room.room_id, otherUserId, KnownMembership.Invite)
    return room;
}

// Megolm-Schlüssel gehen nur an Mitglieder, die im lokalen Raum-State stehen.
// Ohne dieses Warten wird die erste Nachricht nach der Einladung für die
// eingeladene Person unlesbar verschlüsselt.
async function waitForMembership(client, roomId, userId, membership, timeoutMs = 15000) {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
        if (client.getRoom(roomId)?.getMember(userId)?.membership === membership) {
            return true;
        }
        await new Promise((resolve) => setTimeout(resolve, 250));
    }
    console.warn(`Mitgliedschaft ${membership} von ${userId} in ${roomId} nicht rechtzeitig synchronisiert`);
    return false;
}


// check the rooms timeline for a ziege event, return the latest ziege event found or null if none


async function enterTheMatrix(session) {
    let checkedRooms = {} // rooms that we have seen a ziege event for or that we have search the timeline for ziege event of
    let client = null;
    let app = null;

    let lastCryptoPhase = null;
    const cryptoSetup = new CryptoSetup((status) => {
        console.debug("Krypto-Status an Elm: ", status);
        app?.ports.receiveCryptoStatus.send(status);
        if (status.phase === "ready" && lastCryptoPhase !== "ready" && lastCryptoPhase !== null) {
            resendUnsentUpdates();
            recheckAllRooms();
        }
        lastCryptoPhase = status.phase;
    }, {accountManagementUri: session.authMetadata.account_management_uri});

    // Letztes nicht gesendetes Status-Update je Raum. Jedes Update enthält den ganzen
    // Stand, ein späteres ersetzt also ein früheres. Unverifizierte Apps können nicht
    // senden; sobald diese App verifiziert ist, wird nachgeholt.
    const unsentUpdates = new Map();

    // Ein Matrix-Event darf höchstens 64 KB haben; verschlüsselt wird der Stand etwa
    // ein Drittel größer, mehr als rund 48 KB gehen also nicht. Gemessen wird der ganze
    // Stand samt Logo. Ab STATUS_WARN_BYTES wird es eng, gewarnt wird je Raum einmal.
    const STATUS_WARN_BYTES = 30000;
    const warnedRooms = new Set();

    // Das eigene Update geht nach dem Senden direkt an Elm, das daraus u. a. die
    // Raum-ID eines neuen Klienten übernimmt. Über die Timeline käme es nur als
    // lokales, noch unverschlüsseltes Echo an, das isAcceptableStatusUpdate verwirft.
    async function sendStatusUpdate(roomId, payload) {
        const bytes = new TextEncoder().encode(JSON.stringify(payload)).length;
        console.debug(`Status-Update für ${roomId}: ${bytes} Bytes`);
        if (bytes > STATUS_WARN_BYTES && !warnedRooms.has(roomId)) {
            warnedRooms.add(roomId);
            console.warn(`Status-Update für ${roomId}: ${bytes} Bytes`);
            app.ports.receiveStatusSize.send({roomid: roomId, bytes, rejected: false});
        }
        try {
            await client.sendEvent(roomId, ZIEGE_STATUS_UPDATE_MESSAGE_TYPE, payload, "");
            unsentUpdates.delete(roomId);
            emitStatusUpdate(roomId, payload, client.getUserId());
        } catch (e) {
            if (isNotInRoom(e)) {
                // Ein erneuter Versuch scheitert genauso; die Beratung erfährt, dass sie
                // den Eintrag über „Verbindung trennen“ entfernen kann.
                console.error(`Status-Update für ${roomId}: nicht mehr im Raum`, e);
                unsentUpdates.delete(roomId);
                discardUnsentEcho(roomId);
                app.ports.receiveRoomGone.send({roomid: roomId});
                return;
            }
            if (e.errcode === "M_TOO_LARGE" || e.httpStatus === 413) {
                // Ein erneuter Versuch scheitert genauso; erst ein kleinerer Stand geht durch.
                console.error(`Status-Update für ${roomId} zu groß: ${bytes} Bytes`, e);
                unsentUpdates.delete(roomId);
                discardUnsentEcho(roomId);
                app.ports.receiveStatusSize.send({roomid: roomId, bytes, rejected: true});
                return;
            }
            console.error("Status-Update nicht gesendet: ", e);
            unsentUpdates.set(roomId, payload);
            discardUnsentEcho(roomId);
            cryptoSetup.reportSendFailure(e);
        }
    }

    function discardUnsentEcho(roomId) {
        for (const event of client.getRoom(roomId)?.getLiveTimeline().getEvents() ?? []) {
            if (event.status === EventStatus.NOT_SENT) {
                client.cancelPendingEvent(event);
            }
        }
    }

    async function resendUnsentUpdates() {
        for (const [roomId, payload] of [...unsentUpdates]) {
            await sendStatusUpdate(roomId, payload);
        }
    }

    const CHECKEDROOMS_TTL = 30000;

    function store_checkedroom(roomId, e, is_ziege=false) {
        const now = Date.now();
        checkedRooms[roomId] = {"expires": now + CHECKEDROOMS_TTL, "event": e, "is_ziege": is_ziege}
    }


    client = sdk.createClient({
        ...clientOptsForSession(session),
        cryptoCallbacks: cryptoSetup.cryptoCallbacks,
        verificationMethods: ["m.sas.v1"],
    });

    // Räumt alles Lokale ab und lädt neu. Nur einmal, auch wenn mehrere Requests
    // gleichzeitig feststellen, dass die Session beendet ist.
    let forgetting = null;
    function forgetEverything({revoke}) {
        forgetting ??= (async () => {
            client.stopClient();
            if (revoke) {
                try {
                    await revokeSession(session);
                } catch (e) {
                    console.error("Session konnte nicht widerrufen werden: ", e);
                }
            }
            await client.clearStores({cryptoDatabasePrefix: cryptoStorePrefix(session)});
            await forgetSession();
            await PushMaster.unsubscribe();
            await deleteDB();
            localStorage.clear();
            sessionStorage.clear();
            // Nur das Fragment zu ändern löst kein Neuladen aus.
            window.history.replaceState(null, "", window.location.pathname + (revoke ? "" : SESSION_ENDED_HASH));
            window.location.reload();
        })();
        return forgetting;
    }

    // Der Server hat die Session beendet, z. B. über die Kontoverwaltung in MAS
    // oder weil das Refresh-Token nicht mehr gilt. Schon vor dem Start von Elm
    // registriert: Beim Start fallen die ersten Requests genau darauf herein.
    client.on(HttpApiEvent.SessionLoggedOut, () => {
        console.warn("Session wurde serverseitig beendet.")
        forgetEverything({revoke: false});
    })

    // Hält eine andere Seite den Schlüsselspeicher noch offen, etwa eine ältere Ziege
    // ohne Instanzsperre, wartet der Browser beim Öffnen ohne Fehlermeldung. Ist jene
    // eingefroren, gibt sie ihn erst frei, wenn sie geschlossen wird, und auch dann
    // bleibt die wartende Anfrage oft hängen; neu laden hilft.
    showStartupStatus("Schlüsselspeicher wird geöffnet …");
    const storeWarning = setTimeout(() => {
        showStartupWarning("Der Schlüsselspeicher lässt sich nicht öffnen. Meist ist Ziege noch in einem anderen Tab oder Fenster geöffnet. "
            + "Bitte dort schließen und Ziege hier neu laden.");
        showStartupReload(true);
    }, CRYPTO_STORE_WARNING_MS);
    try {
        await deleteForeignCryptoStores(cryptoStorePrefix(session));
        await client.initRustCrypto({storageKey: cryptoStorageKey(session), cryptoDatabasePrefix: cryptoStorePrefix(session)});
        // Ohne gespeicherten Sync-Stand beginnt jeder Start mit einem Initial Sync, und
        // der meldet keine Geräte, die inzwischen hinzugekommen sind. Der Schlüsselspeicher
        // hielte seine Gerätelisten sonst für aktuell: Neue Geräte bekämen keine
        // Schlüssel, ihre Nachrichten und Verifikationsanfragen blieben unbeachtet.
        await client.getCrypto().markAllTrackedUsersAsDirty();
        cryptoSetup.attach(client);
    } catch (e) {
        console.error("Rust-Crypto nicht gestartet: ", e);
        cryptoSetup.attach(client, e);
    } finally {
        clearTimeout(storeWarning);
        showStartupWarning("");
        showStartupReload(false);
    }

    async function handleEvent(event, roomId) {
        const room = client.getRoom(roomId);
        if (!isZiegeRoom(room)) {
            return false;
        }
        // Auch ein verlassener Raum liefert mit dem Austritt noch seine letzten Events;
        // ein Stand daraus brächte den eben entfernten Eintrag zurück.
        if (room.getMyMembership() !== KnownMembership.Join) {
            return false;
        }

        await client.decryptEventIfNeeded(event)
        if (!isAcceptableStatusUpdate(room, event)) {
            return false;
        }

        store_checkedroom(roomId, event, true);
        emitStatusUpdate(roomId, event.getContent(), event.getSender())
        return true
    }


    async function checkRoom(roomId, room=null) {
        const now = Date.now();
        if (roomId in checkedRooms) {
            if (checkedRooms[roomId].expires > now) {
                return checkedRooms[roomId].event;
            }
        }

        if (!room) {
            room = client.getRoom(roomId);
        }

        if(!room) {
            console.error(`Room ${roomId || room} does not exist`);
            store_checkedroom(roomId,null);
            return null;
        }

        // Vor dem Beitritt ist der Verlauf nicht lesbar; ein leeres Ergebnis darf
        // dann nicht gecacht werden, sonst fehlt es direkt nach dem Beitritt.
        if (room.getMyMembership() !== KnownMembership.Join) {
            return null;
        }

        let timelineWindow = new sdk.TimelineWindow(client, room.getUnfilteredTimelineSet());
        let eventId = null; // we can fetch events around a specified event or set to null to go from end of timeline
        let initialWindowSize = 20; // number of events to grab for initial load
        await timelineWindow.load(eventId, initialWindowSize); // load the initial events (required step)
        let moreMessages = false
        do {

            // now we can grab the events
            let events = timelineWindow.getEvents();

            for (const e of [...events].reverse()) {
                await client.decryptEventIfNeeded(e)
                if (isAcceptableStatusUpdate(room, e)) {
                    store_checkedroom(roomId,e);
                    return e;
                }
            }

            // now lets move the window backwards
            // moreMessages will be false if there are no more events in this direction
            let backwards = true;
            moreMessages = await timelineWindow.paginate(backwards ? 'b' : 'f', 8);

            if (moreMessages) {
                // there are more messages, lets remove the previous page's events
                await timelineWindow.unpaginate(8, !backwards);

            }
        } while (moreMessages);

        store_checkedroom(roomId,null);
        return null;
    }

    async function checkRooms() {
        const rooms = await client.getJoinedRooms()
        for (const roomId of rooms.joined_rooms) {
            if (!isZiegeRoom(client.getRoom(roomId))) {
                continue;
            }
            emitRoomMembership(client.getRoom(roomId));

            let event = await checkRoom(roomId)
            store_checkedroom(roomId,event);
            if (event) {
                await handleEvent(event, roomId)
            }
        }
    }

    const elmE = document.getElementById('elm');


    const emptyState =  { "session": null, "clients": [] }
    var data = { "state":emptyState, "userId": client.getUserId(), "deviceId": client.getDeviceId(), "version": ZIEGE_VERSION };

    // Der Stand der App gehört zum Konto: Meldet sich im selben Browser eine andere
    // Person an, sieht sie nicht die Klienten der vorigen. Den Stand unter dem alten
    // Schlüssel hat nur eine Sitzung mit dem alten Krypto-Store geschrieben.
    const stateKey = `ziege:${client.getUserId()}`;
    const legacyState = cryptoStorePrefix(session) === DEFAULT_CRYPTO_STORE_PREFIX
        ? localStorage.getItem(LEGACY_STATE_KEY) : null;
    try {
        const stored = localStorage.getItem(stateKey) ?? legacyState;
        data["state"] = JSON.parse(stored) || emptyState
    } catch (e) {
        console.error("could not load ziege data: ", e)
    }


    // Ohne OpenID-Token startet die App trotzdem; updateAccessToken holt es nach.
    // Ist die Session beendet, räumt der SessionLoggedOut-Handler gerade auf.
    showStartupStatus("Verbindung zum Server …");
    let oidtoken = null;
    try {
        oidtoken = await client.getOpenIdToken();
    } catch (e) {
        if (forgetting) {
            return;
        }
        console.error("OpenID-Token nicht erhalten: ", e);
    }


    data["patternclientconfig"] =
        {
            "url": "https://admin.ziege.social/api/1/texelclient/",
            "ingesturl": "https://admin.ziege.social/api/1/ingest/",
            "favoriteurl":"https://admin.ziege.social/api/1/berater/favorites/",
            "researchurl": "https://admin.ziege.social/api/1/forschung/"
        }
    data["access_token"] =  oidtoken
    data["accountManagementUrl"] = session.authMetadata.account_management_uri || "";

    var pushMaster = new PushMaster()


    data["notification_permission"] = await pushMaster.recheckPermissions(); // FIXME. We can find this out beforehand.

    customElements.define("ziege-progress", ZiegeProgressElement);
    customElements.define("date-progress", DateProgressElement);


    app = Elm.Main.init({
        node: elmE,
        flags: data,
    });
    connectHistory(app);
    app.ports.copyToClipboard.subscribe((text) => {
        navigator.clipboard.writeText(text).catch((error) => console.warn("Zwischenablage: ", error));
    });
    app.ports.receiveCryptoStatus.send({...cryptoSetup.status});


    pushMaster.setCallback(
        async (newPermission) => {
            app.ports.notificationPermissionChanged.send(newPermission)
            if (newPermission == "granted") {
                await pushMaster.updatePush(false);
            }
        })


    // Das Push-Backend und die Termine für den Service Worker brauchen nur einen neuen
    // Stand, wenn sich die anstehenden Termine geändert haben.
    let lastNextUpdate = null;

    app.ports.saveData.subscribe(async function (data) {
        localStorage.setItem(stateKey, JSON.stringify(data));
        localStorage.removeItem(LEGACY_STATE_KEY);

        const nextUpdate = data["nextUpdate"]
        const nextUpdateJson = JSON.stringify(nextUpdate)
        if (nextUpdateJson === lastNextUpdate) {
            return;
        }
        lastNextUpdate = nextUpdateJson;
        await pushMaster.updatePush(false);
        await saveData({id: 1, nextUpdate})
    });

    app.ports.logError.subscribe((message) => console.error("Elm: ", message));

    app.ports.cryptoAction.subscribe(async (action) => {
        await cryptoSetup.handleAction(action);
    })

    app.ports.performLogout.subscribe(async () => {
        await forgetEverything({revoke: true});
    })


    app.ports.requestNotificationPermission.subscribe(async () => {
        await pushMaster.updatePush(true);
    });


    // noinspection JSUnusedLocalSymbols
    async function emitError(message, canContinue) {
        console.warn("Fehlermeldung an Elm: ", message);
        app.ports.receiveMatrixError.send({message, canContinue});
    }


    app.ports.inviteToRoom.subscribe(async function (message) {
        await client.invite(message.roomid, message.userid, "Ziege benutzen")
    })

    app.ports.answerRoomInvite.subscribe(answerInvite)
    app.ports.reinviteToRoom.subscribe(reinvite)

    // Die Apps eines Kontos und ob ihr Besitzer sie verifiziert hat.
    app.ports.requestDevices.subscribe(async (userId) => {
        try {
            const crypto = client.getCrypto();
            const devices = (await crypto.getUserDeviceInfo([userId], true)).get(userId) ?? new Map();
            const list = [];
            for (const [deviceId, device] of devices) {
                const status = await crypto.getDeviceVerificationStatus(userId, deviceId);
                list.push({deviceid: deviceId, name: device.displayName || "", verified: Boolean(status?.signedByOwner)});
            }
            app.ports.receiveDevices.send({userid: userId, devices: list});
        } catch (e) {
            console.error("Apps nicht abgefragt: ", e);
            app.ports.receiveDevices.send({userid: userId, error: e.message});
        }
    });

    // Ist Ziege schon nicht mehr im Raum, etwa nach einem Austritt in einer anderen
    // App, nimmt der Server das Verlassen ohne neuen Austritt hin oder lehnt es ab.
    // Der Eintrag verschwindet in beiden Fällen, sonst ließe er sich nie entfernen.
    app.ports.leaveRoom.subscribe(async function ({roomid}) {
        try {
            await client.leave(roomid);
            emitLeaveRoom(roomid);
        } catch (e) {
            if (isNotInRoom(e)) {
                console.warn("Raum schon verlassen: ", roomid, e);
                emitLeaveRoom(roomid);
            } else {
                console.error("Raum nicht verlassen: ", roomid, e);
            }
        }
    })

    function isNotInRoom(e) {
        return (e.httpStatus === 403 || e.httpStatus === 404)
            && (e.errcode === "M_NOT_FOUND" || /not in room|not a member|unknown room/i.test(e.message ?? ""));
    }


    app.ports.sendMessage.subscribe(async function (message) {
        let roomId = message.roomid
        if (!roomId) {

            let newRoom = await createRoom(client,  message.userid)
            roomId = newRoom.room_id
        }
        console.debug("Status-Update senden: ", roomId);
        await sendStatusUpdate(roomId, message.payload)

    })

    function isStaffed(room) {
        const ownUserId = client.getUserId();
        return room.getJoinedMembers().some((member) => member.userId !== ownUserId);
    }

    // staffed: Außer dieser App ist noch jemand im Raum, oder keiner der eigenen
    // Ziege-Räume hat noch eine Beratung.
    function emitStatusUpdate(roomId, status, sender) {
        console.debug("Status-Update an Elm: ", roomId);
        const room = client.getRoom(roomId);
        const staffed = !room || isStaffed(room) || !client.getRooms().some((other) =>
            isZiegeRoom(other) && other.getMyMembership() === KnownMembership.Join && isStaffed(other));
        app.ports.receiveStatusUpdate.send({ "roomid": roomId, "userid": sender || "", "payload": status, "staffed": staffed })
    }

    // Meldet Elm, wie die übrigen Mitglieder eines Ziege-Raums zum Raum stehen,
    // damit der Berater offene oder abgelehnte Einladungen sieht.
    function emitRoomMembership(room) {
        if (!isZiegeRoom(room)) {
            return;
        }
        const ownUserId = client.getUserId();
        app.ports.receiveRoomMembership.send({
            roomid: room.roomId,
            members: room.currentState.getMembers()
                .filter((member) => member.userId !== ownUserId)
                .map((member) => ({userid: member.userId, name: member.name || member.userId, membership: member.membership})),
        });
    }

    // Eine offene Einladung lässt sich nicht erneut senden; sie wird zurückgezogen
    // und neu verschickt.
    async function reinvite({roomid, userid}) {
        try {
            const membership = client.getRoom(roomid)?.getMember(userid)?.membership;
            if (membership === KnownMembership.Invite) {
                await client.kick(roomid, userid, "Einladung wird erneuert");
            }
            await client.invite(roomid, userid, "Ziege benutzen");
        } catch (e) {
            console.error("Erneute Einladung fehlgeschlagen: ", e);
            await emitError(`Die Einladung konnte nicht erneut gesendet werden: ${e.message}`, true);
        } finally {
            emitRoomMembership(client.getRoom(roomid));
        }
    }

    // Nicht entschlüsselbare Nachrichten in Ziege-Räumen, je Raum und Absender. Ein
    // Problem gilt als erledigt, sobald eine neuere Nachricht desselben Absenders
    // lesbar ist oder die Nachricht selbst nachträglich entschlüsselt wird.
    const decryptionProblems = new Map();
    const lastDecryptedTs = new Map();

    // Ob ein fehlender Schlüssel an dieser App liegt, entscheidet Elm anhand des
    // aktuellen Krypto-Zustands; beim Entschlüsseln steht der oft noch nicht fest.
    function decryptionProblemKind(event) {
        switch (event.decryptionFailureReason) {
            case DecryptionFailureCode.UNSIGNED_SENDER_DEVICE:
            case DecryptionFailureCode.UNKNOWN_SENDER_DEVICE:
                return "sender_unverified";
            case DecryptionFailureCode.MEGOLM_KEY_WITHHELD_FOR_UNVERIFIED_DEVICE:
                return "own_unverified";
            default:
                return "missing_key";
        }
    }

    // Nachrichten von vor der Anmeldung dieser App: Deren Schlüssel kommen, wenn
    // überhaupt, aus dem Key-Backup; ein Hinweis darauf wäre nur Rauschen.
    const HISTORICAL_FAILURES = [
        DecryptionFailureCode.HISTORICAL_MESSAGE_NO_KEY_BACKUP,
        DecryptionFailureCode.HISTORICAL_MESSAGE_BACKUP_UNCONFIGURED,
        DecryptionFailureCode.HISTORICAL_MESSAGE_WORKING_BACKUP,
        DecryptionFailureCode.HISTORICAL_MESSAGE_USER_NOT_JOINED,
    ];

    function emitDecryptionProblems() {
        app.ports.receiveDecryptionProblems.send([...decryptionProblems.values()]);
    }

    // Nachrichten, die sich nicht entschlüsseln ließen. Kommen die Schlüssel später
    // (Key-Backup nach dem Recovery-Key, Weitergabe nach einer Verifikation),
    // entschlüsselt das SDK sie nachträglich; dann muss der Raum neu gelesen werden,
    // sonst bleibt der Stand von vor der Entschlüsselung stehen.
    const undecryptedEvents = new Set();
    const pendingRechecks = new Map();

    // Unlesbar, weil die Absender-App hier nicht als verifiziert galt. Das SDK
    // entschlüsselt nur neu, wenn neue Raumschlüssel ankommen; wird die Absender-App
    // später als verifiziert bekannt, versucht Ziege es selbst noch einmal.
    const untrustedSenderEvents = new Map();

    async function retryUntrustedSenders(userIds) {
        const events = [...untrustedSenderEvents.values()].filter((event) => userIds.includes(event.getSender()));
        for (const event of events) {
            untrustedSenderEvents.delete(event.getId());
            try {
                await event.attemptDecryption(client.getCrypto(), {isRetry: true});
            } catch (e) {
                console.warn("Erneutes Entschlüsseln fehlgeschlagen: ", event.getId(), e);
            }
        }
    }

    // Apps anderer Personen, die ihr Besitzer verifiziert hat, gemerkt über Neustarts.
    // Status-Updates, die vor der Verifikation einer solchen App gesendet wurden,
    // kann sie nicht lesen; kommt eine hinzu, geht der aktuelle Stand in jeden
    // gemeinsamen Raum noch einmal.
    const SIGNED_DEVICES_KEY = "ziege_signed_devices";

    function loadSignedDevices() {
        try {
            return JSON.parse(localStorage.getItem(SIGNED_DEVICES_KEY)) || {};
        } catch (e) {
            return {};
        }
    }

    async function signedDevicesOf(crypto, userId, devices) {
        const signed = [];
        for (const deviceId of devices.get(userId)?.keys() ?? []) {
            const status = await crypto.getDeviceVerificationStatus(userId, deviceId);
            if (status?.signedByOwner) {
                signed.push(deviceId);
            }
        }
        return signed;
    }

    function sharedZiegeRooms(userId) {
        return client.getRooms().filter((room) =>
            isZiegeRoom(room)
            && room.getMyMembership() === KnownMembership.Join
            && room.getMember(userId)?.membership === KnownMembership.Join);
    }

    function requestResend(roomId) {
        console.debug("Status erneut senden, neu verifizierte App im Raum: ", roomId);
        app.ports.resendRoomState.send(roomId);
    }

    async function onDevicesUpdated(userIds) {
        await retryUntrustedSenders(userIds);
        const crypto = client.getCrypto();
        const others = userIds.filter((userId) => userId !== client.getUserId() && sharedZiegeRooms(userId).length > 0);
        if (others.length === 0) {
            return;
        }
        const known = loadSignedDevices();
        const devices = await crypto.getUserDeviceInfo(others);
        for (const userId of others) {
            const signed = await signedDevicesOf(crypto, userId, devices);
            const before = known[userId];
            known[userId] = signed;
            console.debug("Verifizierte Apps von ", userId, ": vorher ", before, ", jetzt ", signed);
            if (!before || signed.some((deviceId) => !before.includes(deviceId))) {
                sharedZiegeRooms(userId).forEach((room) => requestResend(room.roomId));
            }
        }
        try {
            localStorage.setItem(SIGNED_DEVICES_KEY, JSON.stringify(known));
        } catch (e) {
            console.warn("Verifizierte Apps nicht gemerkt: ", e);
        }
    }

    function scheduleRoomRecheck(roomId) {
        clearTimeout(pendingRechecks.get(roomId));
        pendingRechecks.set(roomId, setTimeout(async () => {
            pendingRechecks.delete(roomId);
            delete checkedRooms[roomId];
            const latest = await checkRoom(roomId);
            if (latest) {
                await handleEvent(latest, roomId);
            }
        }, 500));
    }

    async function onEventDecrypted(event) {
        const roomId = event.getRoomId();
        const room = client.getRoom(roomId);
        if (!isZiegeRoom(room)) {
            return;
        }
        const sender = event.getSender();
        const key = `${roomId}|${sender}`;

        if (event.isDecryptionFailure()) {
            undecryptedEvents.add(event.getId());
            if (decryptionProblemKind(event) === "sender_unverified") {
                untrustedSenderEvents.set(event.getId(), event);
            }
            if (HISTORICAL_FAILURES.includes(event.decryptionFailureReason)) {
                return;
            }
            const known = decryptionProblems.get(key);
            if (event.getTs() > (lastDecryptedTs.get(key) ?? 0) && (!known || known.ts <= event.getTs())) {
                decryptionProblems.set(key, {
                    roomid: roomId,
                    sender,
                    sendername: room.getMember(sender)?.name || sender,
                    kind: decryptionProblemKind(event),
                    ts: event.getTs(),
                    eventId: event.getId(),
                });
                emitDecryptionProblems();
            }
            return;
        }

        lastDecryptedTs.set(key, Math.max(event.getTs(), lastDecryptedTs.get(key) ?? 0));
        const wasUndecrypted = undecryptedEvents.delete(event.getId());
        untrustedSenderEvents.delete(event.getId());
        const known = decryptionProblems.get(key);
        if (known && (known.eventId === event.getId() || known.ts <= event.getTs())) {
            decryptionProblems.delete(key);
            emitDecryptionProblems();
        }
        if (wasUndecrypted) {
            scheduleRoomRecheck(roomId);
        }
    }

    // Nach der Verifikation dieser App kommen Schlüssel für ältere Nachrichten;
    // alle Räume werden neu gelesen.
    function recheckAllRooms() {
        checkedRooms = {};
        checkRooms().catch((e) => console.error("Räume nicht neu gelesen: ", e));
    }

    function emitLeaveRoom(roomId) {
        console.debug("Raum verlassen: ", roomId);
        app.ports.receiveRoomSeverance.send({ "roomid": roomId})
    }

    // Einladungen, die nicht in Frage kommen, werden abgelehnt; die übrigen legt
    // Elm dem Nutzer zur Entscheidung vor.
    // Bei einer neuen Einladung meldet das SDK MyMembership, bevor der Raum im Store
    // liegt; der Raum wird deshalb zusätzlich direkt übergeben.
    async function reviewInvites(newRoom = null) {
        const ownUserId = client.getUserId();
        const invites = [];
        const rooms = new Map(client.getRooms().map((room) => [room.roomId, room]));
        if (newRoom) {
            rooms.set(newRoom.roomId, newRoom);
        }
        for (const room of rooms.values()) {
            if (room.getMyMembership() !== KnownMembership.Invite) {
                continue;
            }
            if (!isAcceptableInvite(room, ownUserId)) {
                console.warn("Einladung abgelehnt, kein Ziege-Raum: ", room.roomId);
                await client.leave(room.roomId).catch((e) => console.error("Ablehnen fehlgeschlagen: ", e));
                continue;
            }
            const inviter = inviterOf(room, ownUserId);
            invites.push({
                roomid: room.roomId,
                inviter,
                invitername: room.getMember(inviter)?.name || inviter,
            });
        }
        console.debug("Einladungen an Elm: ", invites);
        app.ports.receiveRoomInvites.send(invites);
    }

    // Nach dem Beitritt kommt der vollständige Raum-State erst mit dem nächsten Sync.
    async function waitForJoinedRoomState(roomid, timeoutMs = 15000) {
        const deadline = Date.now() + timeoutMs;
        while (Date.now() < deadline) {
            const room = client.getRoom(roomid);
            if (room?.getMyMembership() === KnownMembership.Join && isZiegeRoom(room)) {
                return true;
            }
            await new Promise((resolve) => setTimeout(resolve, 500));
        }
        return false;
    }

    async function answerInvite({roomid, accept}) {
        try {
            if (!accept) {
                await client.leave(roomid);
                return;
            }
            await client.joinRoom(roomid);
            if (!await waitForJoinedRoomState(roomid)) {
                console.warn("Beigetretener Raum ist kein Ziege-Raum, wird verlassen: ", roomid);
                await client.leave(roomid);
                await emitError("Die Einladung führte nicht zu einem Ziege-Raum und wurde verworfen.", true);
                return;
            }
            // Beim Beitritt prüft der Membership-Handler den Raum womöglich schon, bevor
            // sein Verlauf geladen ist; dieses Ergebnis gilt nicht.
            delete checkedRooms[roomid];
            const event = await checkRoom(roomid);
            if (event) {
                await handleEvent(event, roomid);
            } else {
                console.debug("Nach dem Beitritt noch kein Status im Raum: ", roomid);
            }
        } catch (e) {
            console.error("Einladung nicht bearbeitet: ", e);
            await emitError(`Die Einladung konnte nicht bearbeitet werden: ${e.message}`, true);
        } finally {
            await reviewInvites();
        }
    }

    // Ein Sync meldet ClientEvent.Sync, nachdem seine Räume verarbeitet sind.
    function nextSync() {
        return new Promise((resolve) => client.once(ClientEvent.Sync, () => resolve()));
    }

    function initEventHandlers(client) {

        client.on(RoomMemberEvent.Membership, async (event/*, member, oldMembership*/) => {
            const roomId = event.getRoomId();
            emitRoomMembership(client.getRoom(roomId));
            window.setTimeout(async () => {

                let anotherEvent = await checkRoom(roomId)
                if (anotherEvent) {
                    await handleEvent(anotherEvent, roomId)
                }
            })
        })

        client.on("Room.timeline", (rawEvent, room) => handleEvent(rawEvent, room.roomId))
        client.on(RoomEvent.MyMembership, async function (room, membership/*, prevMembership*/) {
            if (membership === KnownMembership.Invite) {
                await reviewInvites(room);
                return;
            } else if (membership === KnownMembership.Leave) {
                // Ein Sync kann einen Austritt und den späteren erneuten Beitritt zugleich
                // enthalten, der erste Sync nach dem Start auch ältere; es zählt der Stand,
                // wenn der Sync verarbeitet ist.
                await nextSync();
                if (room.getMyMembership() === KnownMembership.Leave) {
                    await emitLeaveRoom(room.roomId);
                }
                await reviewInvites();
                return;
            }
            let event = await checkRoom(room.roomId, room);
            store_checkedroom(room.roomId, event);
            if (event) {
                await handleEvent(event, room.roomId)
            }
        });
    }


    client.once(ClientEvent.Sync, async (/*state, prevState, res */) => {
        await initEventHandlers(client)
    });

    client.on(ClientEvent.Sync, async (state /*, prevState, res*/) => {
        switch (state) {
            case "PREPARED":
                await cryptoSetup.start();
                await reviewInvites();

                // recheck all rooms.
                checkedRooms = {}
                await checkRooms()

                break
        }

    });

    async function updateAccessToken(initialtoken) {
        let oidtoken = initialtoken;
        try {
            oidtoken ??= await client.getOpenIdToken();
        } catch (e) {
            console.error("OpenID-Token nicht erneuert: ", e);
            window.setTimeout(updateAccessToken, 60 * 1000);
            return;
        }
        app.ports.receiveAccessToken.send(oidtoken)

        // => { access_token, token_type, matrix_server_name, expires_in }
        window.setTimeout(updateAccessToken, Math.max(120 * 1000.0, (oidtoken.expires_in-15) * 1000.0))
    }


    updateAccessToken(oidtoken); // to star the timeout thing.


    // schon vor dem ersten Sync, damit Fehler beim Entschlüsseln des Verlaufs ankommen
    client.on(MatrixEventEvent.Decrypted, (event) => onEventDecrypted(event))
    client.on(CryptoEvent.DevicesUpdated, (userIds) => {
        onDevicesUpdated(userIds).catch((e) => console.error("Geänderte Geräte nicht verarbeitet: ", e));
    });
    client.on(CryptoEvent.UserTrustStatusChanged, (userId) => {
        onDevicesUpdated([userId]).catch((e) => console.error("Geänderte Verifikation nicht verarbeitet: ", e));
    });

    await client.startClient({initialSyncLimit: 20});

}

// Markiert beim Neuladen, dass der Server die Session beendet hat.
const SESSION_ENDED_HASH = "#session-beendet";

function showLogin(errorMessage = null) {
    // Die Anmeldeseite fasst weder Sitzung noch Schlüsselspeicher an; die Rückleitung
    // vom Anmelde-Server kann so in einem anderen Tab oder Fenster ankommen.
    releaseAppLock();
    document.body.innerHTML = `
    <div style="padding: 0.5rem">
    <h1>Leider nicht angemeldet.</h1>
    <h2>Die Anmeldung bei Ziege erfolgt über den Ziege-Anmelde-Server.</h2>
    <p id="login-error" style="color: var(--z-danger-text);"></p>
    <div style="width: 100%;"><button id="login-button" style="margin-left: auto; margin-right: auto; width: 60%; min-width: 15rem; border: 1px solid var(--z-accent); background: none; padding: 0.5rem 1rem 0.5rem 1rem; display: block; font: inherit; color: var(--z-accent); cursor: pointer;">Zur Anmeldung</button></div>
    <img src="static/splash.webp" alt="Cartoon mit Ziege" style="width: 80%; margin-left: auto; display: block; margin-right: auto; padding: 2rem;">
    </div>
    `

    // Kommt die Anmeldeseite aus dem Back-Forward-Cache zurück, etwa beim Rücksprung
    // nach der Anmeldung, zeigt sie den alten Stand; neu geladen startet die App.
    window.addEventListener("pageshow", (event) => {
        if (event.persisted) {
            window.location.reload();
        }
    });

    // Schließt ein anderer Tab oder ein anderes Fenster die Anmeldung ab, gilt sie auch
    // hier; neu geladen wartet diese Seite, bis jene Instanz geschlossen ist.
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible" && hasStoredSession()) {
            window.location.reload();
        }
    });

    const errorE = document.getElementById("login-error");
    if (errorMessage) {
        errorE.textContent = errorMessage;
    }

    document.getElementById("login-button").addEventListener("click", async () => {
        try {
            await startLogin();
        } catch (e) {
            console.error("Anmeldung nicht möglich: ", e);
            errorE.textContent = e.message;
        }
    });
}

export default async function buildZiegeApp() {
    showStartupStatus("Anmeldung wird geprüft …");
    await holdAppLock(() => showStartupWarning(
        "Ziege ist schon in einem anderen Fenster oder Tab geöffnet. Bitte dort schließen, dann geht es hier von selbst weiter."));
    showStartupWarning("");
    await discardLegacySession();
    const discarded = await discardUnusableSession();

    let session = await loadSession();

    const response = authorizationResponse();
    if (response) {
        try {
            session = await completeLogin(response);
            if (returnToLoginStart(response.get("state"))) {
                // Der Rücksprung lädt die App auf dem Eintrag vor der Anmeldung neu.
                // Bleibt er aus, läuft sie hier weiter.
                await new Promise((resolve) => setTimeout(resolve, 3000));
            }
        } catch (e) {
            console.error("Anmeldung fehlgeschlagen: ", e);
            showLogin(e.message);
            return;
        }
    }

    if (!session && window.location.hash === SESSION_ENDED_HASH) {
        window.history.replaceState(null, "", window.location.pathname);
        showLogin("Die Anmeldung ist abgelaufen oder wurde beendet, zum Beispiel in der Kontoverwaltung. Bitte neu anmelden und die App danach mit dem Recovery-Key oder einer anderen App verifizieren.");
        return;
    }

    if (!session) {
        showLogin(discarded
            ? "Die Anmeldung dieser App wurde aus Sicherheitsgründen erneuert. Bitte neu anmelden und die App danach mit dem Recovery-Key oder einer anderen App verifizieren."
            : null);
        return;
    }

    console.log(`Angemeldet als ${session.userId} (${session.deviceId})`);
    showStartupAccount(session.userId, session.deviceId);
    await enterTheMatrix(session);
}
